import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/config';
import GradientBackground from '../components/GradientBackground';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ASSETS } from '../utils/assets';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Refs for scroll animations
  const heroRef = useRef<HTMLElement>(null);
  const showcaseRef = useRef<HTMLElement>(null);
  const featuresRef = useRef<HTMLElement>(null);
  const ctaRef = useRef<HTMLElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  // Animation states
  const [visibleSections, setVisibleSections] = useState<Set<string>>(
    new Set()
  );
  const [showStoreModal, setShowStoreModal] = useState(false);


  // Intersection Observer setup
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '-50px 0px -50px 0px',
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const sectionId = entry.target.getAttribute('data-section');
        if (sectionId) {
          setVisibleSections((prev) => {
            const newSet = new Set(prev);
            if (entry.isIntersecting) {
              newSet.add(sectionId);
            } else {
              newSet.delete(sectionId);
            }
            return newSet;
          });
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback, 
      observerOptions
    );

    // Observe all sections
    const sections = [
      heroRef.current,
      showcaseRef.current,
      featuresRef.current,
      ctaRef.current,
      footerRef.current,
    ];

    sections.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  // Listen for URL parameter changes
  useEffect(() => {
    const checkLanguage = () => {
      const params = new URLSearchParams(window.location.search);
      const urlLang = params.get('lang');
      if (urlLang && (urlLang === 'en' || urlLang === 'de')) {
        i18n.changeLanguage(urlLang);
      }
    };

    checkLanguage();
    window.addEventListener('popstate', checkLanguage);
    
    return () => {
      window.removeEventListener('popstate', checkLanguage);
    };
  }, []);

  // Animation styles helper
  const getSectionStyle = (sectionId: string, delay: number = 0) => ({
    opacity: visibleSections.has(sectionId) ? 1 : 0,
    transform: visibleSections.has(sectionId) 
      ? 'translateY(0px)' 
      : 'translateY(40px)',
    transition: `all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms`,
  });

  const features = [
    {
      icon: '🗳️',
      title: t('homepage.features.noMoreDebates.title'),
      description: t('homepage.features.noMoreDebates.description')
    },
    {
      icon: '🤝',
      title: t('homepage.features.shareBurden.title'),
      description: t('homepage.features.shareBurden.description')
    },
    {
      icon: '⚡',
      title: t('homepage.features.quickCreation.title'),
      description: t('homepage.features.quickCreation.description')
    },
    {
      icon: '💡',
      title: t('homepage.features.crowdsource.title'),
      description: t('homepage.features.crowdsource.description')
    },
    {
      icon: '📱',
      title: t('homepage.features.liveUpdates.title'),
      description: t('homepage.features.liveUpdates.description')
    },
    {
      icon: '🎨',
      title: t('homepage.features.beautifulEvents.title'),
      description: t('homepage.features.beautifulEvents.description')
    }
  ];

  const handleGetStarted = () => {
    setShowStoreModal(true);
  };

  const handleViewDemo = () => {
    navigate('/event/demo');
  };


  return (
    <>
      <GradientBackground backgroundIndex={0} />
      <div style={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        zIndex: 1,
        overflow: 'hidden',
      }}>
        {/* Navigation */}
        <Navigation />

        {/* Hero Section */}
        <section 
          ref={heroRef}
          data-section="hero"
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            padding: '0 24px',
            position: 'relative',
            ...getSectionStyle('hero'),
          }}>
          {/* Floating particles effect */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            overflow: 'hidden',
          }}>
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  width: '4px',
                  height: '4px',
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  borderRadius: '50%',
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
                  animationDelay: `${Math.random() * 2}s`,
                }}
              />
            ))}
          </div>

          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            position: 'relative',
            zIndex: 2,
          }}>
            {/* Logo */}
            <div style={{
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
            }}>
              <img 
                src={ASSETS.LOGO} 
                alt="Allez-Hopp Logo"
                style={{
                  width: '80px',
                  height: '80px',
                  filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
                }}
              />
              <div style={{
                fontSize: '64px',
                fontWeight: '600',
                color: 'white',
                textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                letterSpacing: '-0.02em',
              }}>
                Allez-Hopp
              </div>
            </div>

            {/* Tagline */}
            <h1 style={{
              fontSize: '48px',
              fontWeight: '600',
              color: 'white',
              marginBottom: '24px',
              textShadow: '0 4px 8px rgba(0, 0, 0, 0.4)',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              letterSpacing: '-0.02em',
              lineHeight: '1.1',
            }}>
              {t('homepage.hero.tagline')}
            </h1>

            <p style={{
              fontSize: '22px',
              color: 'rgba(255, 255, 255, 0.95)',
              marginBottom: '48px',
              lineHeight: '1.7',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: '400',
              maxWidth: '680px',
              margin: '0 auto 48px',
            }}>
              {t('homepage.hero.description')}
            </p>

            {/* CTA Buttons */}
            <div style={{
              display: 'flex',
              gap: '24px',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: '80px',
            }}>
              <button
                onClick={handleGetStarted}
                style={{
                  padding: '20px 40px',
                  fontSize: '18px',
                  fontWeight: '600',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: 'none',
                  borderRadius: '16px',
                  color: '#1A1A2E',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                  letterSpacing: '-0.01em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)';
                }}
              >
                {t('homepage.hero.startPlanning')}
              </button>
              <button
                onClick={handleViewDemo}
                style={{
                  padding: '20px 40px',
                  fontSize: '18px',
                  fontWeight: '600',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  backgroundColor: 'transparent',
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                  borderRadius: '16px',
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backdropFilter: 'blur(20px)',
                  letterSpacing: '-0.01em',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                }}
              >
                {t('homepage.hero.demo')}
              </button>
            </div>

            {/* App Preview */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
              flexWrap: 'wrap',
              marginTop: '40px',
            }}>
              <div style={{
                width: '200px',
                height: '400px',
                borderRadius: '20px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                transform: 'rotate(-5deg)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(-2deg) scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(-5deg) scale(1)';
              }}>
                <img 
                  src={ASSETS.EVENT_CREATION_SCREEN} 
                  alt="Allez-Hopp Event Creation Screen"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
              <div style={{
                width: '200px',
                height: '400px',
                borderRadius: '20px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                transform: 'rotate(0deg)',
                transition: 'all 0.3s ease',
                zIndex: 2,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(0deg) scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(0deg) scale(1)';
              }}>
                <img 
                  src={ASSETS.EVENT_LIST_SCREEN} 
                  alt="Allez-Hopp Event List Screen"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
              <div style={{
                width: '200px',
                height: '400px',
                borderRadius: '20px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                transform: 'rotate(5deg)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'rotate(2deg) scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'rotate(5deg) scale(1)';
              }}>
                <img 
                  src={ASSETS.EVENT_SCREEN} 
                  alt="Allez-Hopp Event Screen"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* App Showcase Section */}
        <section 
          ref={showcaseRef}
          data-section="showcase"
          style={{
            padding: '80px 24px',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)',
            ...getSectionStyle('showcase'),
          }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
          }}>
            <h2 style={{
              fontSize: '48px',
              fontWeight: '600',
              color: 'white',
              textAlign: 'center',
              marginBottom: '20px',
              textShadow: '0 4px 8px rgba(0, 0, 0, 0.4)',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              letterSpacing: '-0.02em',
            }}>
              {t('homepage.showcase.title')}
            </h2>
            <p style={{
              fontSize: '22px',
              color: 'rgba(255, 255, 255, 0.9)',
              textAlign: 'center',
              marginBottom: '60px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: '400',
              maxWidth: '600px',
              margin: '0 auto 60px',
            }}>
              {t('homepage.showcase.description')}
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '30px',
              alignItems: 'center',
            }}>
              {/* Home Screen */}
              <div style={{
                textAlign: 'center',
                opacity: visibleSections.has('showcase') ? 1 : 0,
                transform: visibleSections.has('showcase') 
                  ? 'translateY(0px)' 
                  : 'translateY(40px)',
                transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: visibleSections.has('showcase') 
                  ? '200ms' 
                  : '0ms',
              }}>
                <div style={{
                  width: '200px',
                  height: '400px',
                  margin: '0 auto 20px',
                  borderRadius: '25px',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  overflow: 'hidden',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)';
                }}>
                  <img 
                    src={ASSETS.HOME_SCREEN} 
                    alt="Allez-Hopp Home Screen"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '500',
                  color: 'white',
                  marginBottom: '8px',
                  fontFamily: 'Roboto, sans-serif',
                }}>
                  {t('homepage.showcase.homeDashboard')}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.8)',
                }}>
                  {t('homepage.showcase.homeDashboardDesc')}
                </p>
              </div>

              {/* Event Creation Screen */}
              <div style={{
                textAlign: 'center',
                opacity: visibleSections.has('showcase') ? 1 : 0,
                transform: visibleSections.has('showcase') 
                  ? 'translateY(0px)' 
                  : 'translateY(40px)',
                transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: visibleSections.has('showcase') 
                  ? '400ms' 
                  : '0ms',
              }}>
                <div style={{
                  width: '200px',
                  height: '400px',
                  margin: '0 auto 20px',
                  borderRadius: '25px',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  overflow: 'hidden',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)';
                }}>
                  <img 
                    src={ASSETS.EVENT_CREATION_SCREEN} 
                    alt="Allez-Hopp Event Creation Screen"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '500',
                  color: 'white',
                  marginBottom: '8px',
                  fontFamily: 'Roboto, sans-serif',
                }}>
                  {t('homepage.showcase.createEvent')}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.8)',
                }}>
                  {t('homepage.showcase.createEventDesc')}
                </p>
              </div>

              {/* Event List Screen */}
              <div style={{
                textAlign: 'center',
                opacity: visibleSections.has('showcase') ? 1 : 0,
                transform: visibleSections.has('showcase') 
                  ? 'translateY(0px)' 
                  : 'translateY(40px)',
                transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: visibleSections.has('showcase') 
                  ? '600ms' 
                  : '0ms',
              }}>
                <div style={{
                  width: '200px',
                  height: '400px',
                  margin: '0 auto 20px',
                  borderRadius: '25px',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  overflow: 'hidden',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)';
                }}>
                  <img 
                    src={ASSETS.EVENT_LIST_SCREEN} 
                    alt="Allez-Hopp Event List Screen"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '500',
                  color: 'white',
                  marginBottom: '8px',
                  fontFamily: 'Roboto, sans-serif',
                }}>
                  {t('homepage.showcase.eventList')}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.8)',
                }}>
                  {t('homepage.showcase.eventListDesc')}
                </p>
              </div>

              {/* Event Screen */}
              <div style={{
                textAlign: 'center',
                opacity: visibleSections.has('showcase') ? 1 : 0,
                transform: visibleSections.has('showcase') 
                  ? 'translateY(0px)' 
                  : 'translateY(40px)',
                transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: visibleSections.has('showcase') 
                  ? '800ms' 
                  : '0ms',
              }}>
                <div style={{
                  width: '200px',
                  height: '400px',
                  margin: '0 auto 20px',
                  borderRadius: '25px',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  overflow: 'hidden',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(0, 0, 0, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)';
                }}>
                  <img 
                    src={ASSETS.EVENT_SCREEN} 
                    alt="Allez-Hopp Event Screen"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '500',
                  color: 'white',
                  marginBottom: '8px',
                  fontFamily: 'Roboto, sans-serif',
                }}>
                  {t('homepage.showcase.eventDetails')}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.8)',
                }}>
                  {t('homepage.showcase.eventDetailsDesc')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section 
          ref={featuresRef}
          data-section="features"
          style={{
            padding: '80px 24px',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(10px)',
            ...getSectionStyle('features'),
          }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
          }}>
            <h2 style={{
              fontSize: '48px',
              fontWeight: '600',
              color: 'white',
              textAlign: 'center',
              marginBottom: '20px',
              textShadow: '0 4px 8px rgba(0, 0, 0, 0.4)',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              letterSpacing: '-0.02em',
            }}>
              {t('homepage.features.title')}
            </h2>
            <p style={{
              fontSize: '22px',
              color: 'rgba(255, 255, 255, 0.9)',
              textAlign: 'center',
              marginBottom: '60px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: '400',
              maxWidth: '700px',
              margin: '0 auto 60px',
            }}>
              {t('homepage.features.description')}
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '32px',
            }}>
              {features.map((feature, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    borderRadius: '24px',
                    padding: '40px 32px',
                    textAlign: 'center',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(20px)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    opacity: visibleSections.has('features') ? 1 : 0,
                    transform: visibleSections.has('features') 
                      ? 'translateY(0px)' 
                      : 'translateY(60px)',
                    transitionDelay: visibleSections.has('features') 
                      ? `${index * 150}ms` 
                      : '0ms',
                  }}
                  onMouseEnter={(e) => {
                    if (visibleSections.has('features')) {
                      e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (visibleSections.has('features')) {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.12)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                >
                  <div style={{
                    fontSize: '56px',
                    marginBottom: '24px',
                    filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))',
                  }}>
                    {feature.icon}
                  </div>
                  <h3 style={{
                    fontSize: '26px',
                    fontWeight: '600',
                    color: 'white',
                    marginBottom: '16px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    letterSpacing: '-0.01em',
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{
                    fontSize: '17px',
                    color: 'rgba(255, 255, 255, 0.85)',
                    lineHeight: '1.7',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    fontWeight: '400',
                  }}>
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section 
          ref={ctaRef}
          data-section="cta"
          style={{
            padding: '80px 24px',
            textAlign: 'center',
            ...getSectionStyle('cta'),
          }}>
          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
          }}>
            <h2 style={{
              fontSize: '44px',
              fontWeight: '600',
              color: 'white',
              marginBottom: '24px',
              textShadow: '0 4px 8px rgba(0, 0, 0, 0.4)',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              letterSpacing: '-0.02em',
            }}>
              {t('homepage.cta.title')}
            </h2>
            <p style={{
              fontSize: '22px',
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '48px',
              lineHeight: '1.7',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: '400',
              maxWidth: '600px',
              margin: '0 auto 48px',
            }}>
              {t('homepage.cta.description')}
            </p>
            <button
              onClick={handleGetStarted}
              style={{
                padding: '24px 48px',
                fontSize: '20px',
                fontWeight: '600',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: 'none',
                borderRadius: '20px',
                color: '#1A1A2E',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                letterSpacing: '-0.01em',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)';
              }}
            >
              {t('homepage.cta.button')}
            </button>
          </div>
        </section>


        {/* Footer */}
        <div 
          ref={footerRef}
          data-section="footer"
          style={getSectionStyle('footer')}
        >
          <Footer />
        </div>
      </div>

      {/* CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>

      {/* Store Selection Modal */}
      {showStoreModal && (
        <div
          onClick={() => setShowStoreModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '600',
              color: '#1A1A2E',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            }}>
              {t('homepage.modal.title')}
            </h2>
            <p style={{
              margin: 0,
              fontSize: '13px',
              color: '#666',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            }}>
              {t('homepage.modal.description')}
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'center',
            }}>
              <button
                onClick={() => {
                  window.open(
                    'https://apps.apple.com/us/app/allezhopp-event-planning/id6752710469',
                    '_blank'
                  );
                  setShowStoreModal(false);
                }}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  color: '#1A1A2E',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: 'fit-content',
                  minWidth: '200px',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 9.11 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" fill="currentColor"/>
                </svg>
                {t('homepage.modal.appStore')}
              </button>
              <button
                onClick={() => {
                  window.open(
                    'https://play.google.com/store/apps/details?id=app.allezhopp.events&pcampaignid=web_share',
                    '_blank'
                  );
                  setShowStoreModal(false);
                }}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  color: '#1A1A2E',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: 'fit-content',
                  minWidth: '200px',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" fill="currentColor"/>
                </svg>
                {t('homepage.modal.googlePlay')}
              </button>
            </div>
            <button
              onClick={() => setShowStoreModal(false)}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                color: '#666',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  marginTop: '0',
                  width: 'fit-content',
                  alignSelf: 'center',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
              }}
            >
              {t('homepage.modal.cancel')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default HomePage;
